import { __extends, __rest, __spreadArray, __awaiter, __generator, __assign } from 'tslib';
import { LocalizedText } from '@shared-ui/localization-context';
import * as React from 'react';
import { createContext, useState, useRef, useImperativeHandle, useMemo, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import { ScriptLoader, classNames } from 'uitk-react-utils';
import { getMarkerPopup, UitkIcon } from 'uitk-react-icons';
import * as ReactDOM from 'react-dom';
import { BrandProvider, useBrand } from '@shared-ui/brand-context';
import '@eg-maps/commons';
import '@eg-maps/base';
import { defaultConfig, EGMap } from '@eg-maps/core';
import { EGDistances } from 'eg-distances';
import '@eg-maps/cluster-supercluster';
import '@eg-maps/google-maps';
import '@eg-maps/mapbox';
import { mergeAndConcat } from 'merge-anything';
import { EGStaticMap } from '@eg-maps/static';

var UitkMap = /** @class */ (function (_super) {
    __extends(UitkMap, _super);
    // **** Static props and methods ends
    function UitkMap(props) {
        var _this = _super.call(this, props) || this;
        // States
        _this.state = {
            currentLocation: {
                lat: props.center.lat,
                lng: props.center.lng
            },
            google: undefined,
            map: undefined,
            previousZoom: undefined,
            zoom: props.zoom
        };
        return _this;
    }
    UitkMap.prototype.componentDidMount = function () {
        var _this = this;
        var google = this.state.google;
        var pageName = this.props.pageName;
        if (!google || !google.maps) {
            var _a = this.props, apiKey = _a.apiKey, locale = _a.locale, countryCode = _a.countryCode;
            var nameForPage = pageName || 'Map';
            var mapApiKey = apiKey || "AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68";
            var url = "//maps.googleapis.com/maps/api/js?key=" + mapApiKey + "&language=" + locale + "&channel=expedia-" + nameForPage + "&region=" + countryCode + "&libraries=places";
            ScriptLoader.getScript(url)
                .then(function () {
                _this.setState({
                    google: window.google
                });
            })
                .catch(function (err) {
                console.error(err);
            });
        }
    };
    UitkMap.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a = this.state, currentLocation = _a.currentLocation, google = _a.google;
        // If they aren't equal, that's what changed
        if (prevState.google !== google) {
            this.startGoogleMaps();
        }
        if (prevState.currentLocation.lat !== currentLocation.lat ||
            prevState.currentLocation.lng !== currentLocation.lng) {
            this.recenterMap();
        }
    };
    UitkMap.prototype.componentWillUnmount = function () {
        var map = this.state.map;
        var event = this.getGoogleMapsEvent();
        if (map && event && event.clearInstanceListeners && typeof event.clearInstanceListeners === 'function') {
            event.clearInstanceListeners(map);
        }
        this.setState({
            map: undefined
        });
    };
    UitkMap.getDerivedStateFromProps = function (nextProps) {
        return {
            currentLocation: {
                lat: nextProps.center.lat,
                lng: nextProps.center.lng
            }
        };
    };
    UitkMap.prototype.getGoogleMapsEvent = function () {
        var google = this.state.google;
        return this.state && google && google.maps && google.maps.event;
    };
    UitkMap.prototype.recenterMap = function () {
        var _a = this.state, google = _a.google, map = _a.map, currentLocation = _a.currentLocation;
        var maps = google && google.maps;
        if (map && maps) {
            var center = new maps.LatLng(currentLocation.lat, currentLocation.lng);
            map.panTo(center);
        }
    };
    UitkMap.prototype.startGoogleMaps = function () {
        var _this = this;
        var _a = this.state, currentLocation = _a.currentLocation, google = _a.google, zoom = _a.zoom;
        var _b = this.props, bounds = _b.bounds, clickablePOIs = _b.clickablePOIs, hideGooglePOIs = _b.hideGooglePOIs;
        var _c = google.maps, Map = _c.Map, LatLngBounds = _c.LatLngBounds, ControlPosition = _c.ControlPosition, event = _c.event;
        var customStyles;
        if (hideGooglePOIs) {
            customStyles = [
                {
                    elementType: 'labels',
                    featureType: 'poi',
                    stylers: [{ visibility: 'off' }]
                },
                {
                    featureType: 'transit',
                    stylers: [{ visibility: 'off' }]
                }
            ];
        }
        else {
            customStyles = [];
        }
        var mapConfig = {
            center: currentLocation,
            fullscreenControl: false,
            gestureHandling: 'greedy',
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: true,
            streetViewControlOptions: {
                position: ControlPosition.RIGHT_TOP
            },
            styles: customStyles,
            zoomControl: true,
            zoomControlOptions: {
                position: ControlPosition.RIGHT_TOP
            }
        };
        if (typeof clickablePOIs !== 'undefined') {
            mapConfig.clickableIcons = clickablePOIs;
        }
        if (zoom) {
            mapConfig.zoom = zoom;
        }
        var map = new Map(this.mapElementRef, mapConfig);
        if (bounds && bounds.sw && bounds.ne) {
            // To make sure only adds when this.props.bounds has props is defined.
            event.addListenerOnce(map, 'bounds_changed', function () {
                _this.setState({
                    zoom: map.getZoom()
                });
            });
            map.fitBounds(new LatLngBounds(bounds.sw, bounds.ne));
        }
        this.setState({
            map: map
        }, this.attachEventListenersToMap);
    };
    UitkMap.prototype.attachEventListenersToMap = function () {
        var map = this.state.map;
        var _a = this.props, onPan = _a.onPan, onPanTimeoutInterval = _a.onPanTimeoutInterval;
        if (onPan && map) {
            map.addListener('dragend', debounce(onPanTimeoutInterval, true, function () {
                onPan(map.center, map.getZoom());
            }));
        }
        var mapZoomEventHandler = function () {
            var _this = this;
            var _a = this.state, previousZoom = _a.previousZoom, zoom = _a.zoom;
            var _b = this.props, onZoomIn = _b.onZoomIn, onZoomOut = _b.onZoomOut, onZoomTimeoutInterval = _b.onZoomTimeoutInterval;
            map.addListener('zoom_changed', debounce(onZoomTimeoutInterval, true, function () {
                _this.setState({
                    previousZoom: zoom,
                    zoom: map.getZoom()
                });
                if (onZoomIn && zoom > previousZoom) {
                    onZoomIn(map.center, map.getZoom());
                }
                else if (onZoomOut && zoom < previousZoom) {
                    onZoomOut(map.center, map.getZoom());
                }
            }));
        };
        var event = this.getGoogleMapsEvent();
        if (map && event && event.addListenerOnce && typeof event.addListenerOnce === 'function') {
            event.addListenerOnce(map, 'tilesloaded', mapZoomEventHandler.bind(this));
        }
    };
    UitkMap.prototype.renderChildren = function () {
        var children = this.props.children;
        var _a = this.state, google = _a.google, map = _a.map;
        // Returns undefined if `this.props.children` is undefined
        return React.Children.map(children, function (child) {
            return React.cloneElement(child, {
                google: google,
                map: map
            });
        });
    };
    UitkMap.prototype.render = function () {
        var _this = this;
        var mapElementRef = function (r) {
            _this.mapElementRef = r;
        };
        return (React.createElement("section", { className: "uitk-map-viewer-map" },
            React.createElement("div", { className: "uitk-map-viewer-gmap", ref: mapElementRef },
                this.renderChildren(),
                React.createElement("p", { className: "uitk-map-viewer-loading" },
                    React.createElement(LocalizedText, { message: "loading" })))));
    };
    UitkMap.displayName = 'UitkMap';
    // *** Static props and methods start
    UitkMap.defaultProps = {
        onPanTimeoutInterval: 100,
        onZoomTimeoutInterval: 100,
        pageName: 'Map'
    };
    return UitkMap;
}(React.PureComponent));

/**
 * A customized popup on the map.
 *
 * @param {!google.maps.LatLng} position
 * @param {!Element} content
 * @constructor
 * @extends {google.maps.OverlayView}
 */
var Marker$1 = /** @class */ (function () {
    function Marker(_a) {
        var position = _a.position, props = __rest(_a, ["position"]);
        if (!position) {
            throw new ReferenceError('`position` required');
        }
        if (!props.google) {
            throw new ReferenceError('`google` required');
        }
        Object.assign(this, props);
        this.position =
            position instanceof this.google.maps.LatLng ? position : new this.google.maps.LatLng(position.lat, position.lng);
        this.props = props;
        // Optionally stop clicks, etc., from bubbling up to the map.
        this.stopPropagation();
        this.popup = new this.google.maps.OverlayView();
        this.popup.anchor = document.createElement('div');
        this.popup.anchor.classList.add('uitk-marker-popup');
        this.popup.onAdd = this.onAdd.bind(this);
        this.popup.draw = this.draw.bind(this);
        this.popup.onRemove = this.onRemove.bind(this);
        this.popup.stopPropagation = this.stopPropagation.bind(this);
    }
    Marker.prototype.focus = function (zIndex) {
        this.popup.anchor.classList.add('uitk-marker-popup-selected');
        if (zIndex) {
            this.popup.anchor.style.zIndex = zIndex;
        }
    };
    Marker.prototype.blur = function () {
        this.popup.anchor.classList.remove('uitk-marker-popup-selected');
    };
    Marker.prototype.onAdd = function () {
        this.popup.getPanes().floatPane.appendChild(this.popup.anchor);
    };
    Marker.prototype.onRemove = function () {
        if (this.popup.anchor.parentElement) {
            this.popup.anchor.parentElement.removeChild(this.popup.anchor);
        }
    };
    Marker.prototype.draw = function () {
        var divPosition = this.popup.getProjection().fromLatLngToDivPixel(this.position);
        // Hide the popup when it is far out of view.
        var display = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ? 'block' : 'none';
        if (display === 'block') {
            this.popup.anchor.style.left = divPosition.x + "px";
            this.popup.anchor.style.top = divPosition.y + "px";
        }
        if (this.popup.anchor.style.display !== display) {
            this.popup.anchor.style.display = display;
        }
    };
    Marker.prototype.stopPropagation = function () {
        var anchor = this.popup && this.popup.anchor;
        if (anchor) {
            anchor.style.cursor = 'auto';
            ['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart', 'pointerdown'].forEach(function (event) {
                anchor.addEventListener(event, function (e) {
                    e.stopPropagation();
                });
            });
        }
    };
    return Marker;
}());

/* eslint-disable prefer-arrow/prefer-arrow-functions */
// eslint-disable-next-line max-params
function defineMarkerSvg(height, markerSvg, viewBox, width, hasHeartIcon) {
    if (hasHeartIcon === void 0) { hasHeartIcon = false; }
    var markerIcon = hasHeartIcon ? "<path d=\"M19 13H5v-2h14v2z\"></path>" : markerSvg;
    return getMarkerPopup({
        attrs: {
            height: height,
            viewBox: viewBox,
            width: width
        },
        data: markerIcon
    });
}
function defineLocationPopup(locationMarkerSvg) {
    return "<div class=\"uitk-marker-popup-anchor location-popup-anchor\">\n    <div class=\"uitk-marker-popup-content uitk-marker-popup-content-location\">\n        " + locationMarkerSvg + "\n    </div>\n  </div>";
}

var defineSvg$5 = function () {
    return defineMarkerSvg(16, "<path d=\"M2.42431 9.50549L4.33268 10.1374L7.01199 7.45809L1.67864 4.39964L2.44958 3.62871C2.48328 3.59501 2.52541 3.57184 2.57596 3.5592C2.62651 3.54656 2.67707 3.54867 2.72762 3.56552L8.92036 5.54972C8.92036 5.54972 9.04464 5.42545 9.29319 5.1769C9.54174 4.92834 9.83664 4.63345 10.1779 4.29222C10.5191 3.95099 10.8688 3.60133 11.2268 3.24325C11.5849 2.88516 11.8777 2.59659 12.1052 2.37753C12.3158 2.15846 12.5728 1.99627 12.8761 1.89095C13.1794 1.78564 13.4575 1.71191 13.7102 1.66979C13.7945 1.66136 13.8767 1.66979 13.9567 1.69506C14.0367 1.72034 14.1105 1.76247 14.1779 1.82144C14.2368 1.88042 14.279 1.95204 14.3042 2.03629C14.3295 2.12055 14.3379 2.2048 14.3295 2.28906C14.2874 2.54182 14.2137 2.81986 14.1084 3.12318C14.003 3.4265 13.8408 3.68348 13.6218 3.89411C13.369 4.14688 13.0615 4.45441 12.6992 4.8167C12.3369 5.179 11.9893 5.52866 11.6565 5.86568C11.3237 6.20269 11.0394 6.48916 10.8035 6.72508C10.5675 6.96099 10.4496 7.07895 10.4496 7.07895L12.4338 13.2717C12.4506 13.3222 12.4527 13.3707 12.4401 13.417C12.4275 13.4634 12.4043 13.5076 12.3706 13.5497L11.587 14.3207L8.54121 8.97469L5.86191 11.6666L6.49382 13.5624L5.73552 14.3333L4.07992 11.9194L1.66602 10.2638L2.43695 9.50549L2.42431 9.50549Z\"/>", '0 0 16 16', 16);
};
var definePopup$7 = function () { return defineLocationPopup(defineSvg$5()); };
var AirportMarker = /** @class */ (function (_super) {
    __extends(AirportMarker, _super);
    function AirportMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.innerHTML = definePopup$7();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg$5();
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    AirportMarker.prototype.getPosition = function () {
        return this.position;
    };
    return AirportMarker;
}(Marker$1));

var defineSvg$4 = function () {
    return defineMarkerSvg(20, "<path d=\"M20.766 19.909l-.408-.97a.424.424 0 0 0-.136-.161.347.347 0 0 0-.204-.06h-.732V7.712a.355.355 0 0 0-.102-.247.333.333 0 0 0-.255-.11h-3.266V3.357a.355.355 0 0 0-.102-.246.333.333 0 0 0-.255-.111H5.864a.333.333 0 0 0-.255.11.355.355 0 0 0-.102.247v15.361h-.731c-.08 0-.148.02-.204.06a.424.424 0 0 0-.137.161l-.408.97a.381.381 0 0 0-.025.179.31.31 0 0 0 .06.161.328.328 0 0 0 .29.17h16.09a.328.328 0 0 0 .29-.17.31.31 0 0 0 .06-.161.381.381 0 0 0-.026-.179zM9.862 16.065H7.685v-2.178h2.177v2.178zm0-4.355H7.685V9.532h2.177v2.178zm0-4.355H7.685V5.177h2.177v2.178zm3.623 11.125h-2.177v-4.593h2.177v4.593zm0-6.77h-2.177V9.532h2.177v2.178zm0-4.355h-2.177V5.177h2.177v2.178zm3.624 8.71H14.93v-2.178h2.178v2.178zm0-4.355H14.93V9.532h2.178v2.178z\"/>", '0 0 24 24', 20);
};
var definePopup$6 = function () {
    return "<div class=\"uitk-marker-popup-anchor hotel-popup-anchor\">\n    <div class=\"uitk-marker-popup-content uitk-marker-popup-content-hotel\">\n      " + defineSvg$4() + "\n    </div>\n  </div>";
};
var HotelMarker = /** @class */ (function (_super) {
    __extends(HotelMarker, _super);
    function HotelMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.setAttribute('type', 'hotel');
        _this.popup.anchor.innerHTML = definePopup$6();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg$4();
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    HotelMarker.prototype.getPosition = function () {
        return this.position;
    };
    return HotelMarker;
}(Marker$1));

var defineSvg$3 = function () {
    return defineMarkerSvg(16, "<path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M3.33398 6.00004C3.33398 3.42004 5.42065 1.33337 8.00065 1.33337C10.5807 1.33337 12.6673 3.42004 12.6673 6.00004C12.6673 9.50004 8.00065 14.6667 8.00065 14.6667C8.00065 14.6667 3.33398 9.50004 3.33398 6.00004ZM6.33398 6.00004C6.33398 6.92004 7.08065 7.66671 8.00065 7.66671C8.92065 7.66671 9.66732 6.92004 9.66732 6.00004C9.66732 5.08004 8.92065 4.33337 8.00065 4.33337C7.08065 4.33337 6.33398 5.08004 6.33398 6.00004Z\"/>", '0 0 16 16', 16);
};
var definePopup$5 = function () { return defineLocationPopup(defineSvg$3()); };
var PlaceMarker = /** @class */ (function (_super) {
    __extends(PlaceMarker, _super);
    function PlaceMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.innerHTML = definePopup$5();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg$3();
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    PlaceMarker.prototype.getPosition = function () {
        return this.position;
    };
    return PlaceMarker;
}(Marker$1));

// eslint-disable-next-line @typescript-eslint/no-unused-vars
var defineHeartSvg = function (_a) {
    _a.hasHeartIcon;
    return defineMarkerSvg(14, "<path d=\"M12 21l-8.438-8.689a5.358 5.358 0 0 1-1.171-1.806 5.624 5.624 0 0 1 0-4.114c.26-.669.65-1.27 1.171-1.806a5.126 5.126 0 0 1 1.739-1.184A5.174 5.174 0 0 1 7.283 3c.67 0 1.332.134 1.983.401.65.268 1.23.663 1.738 1.184L12 5.61l.996-1.024a5.126 5.126 0 0 1 1.738-1.184A5.174 5.174 0 0 1 16.717 3c.67 0 1.331.134 1.982.401.651.268 1.23.663 1.738 1.184.521.535.912 1.137 1.172 1.806a5.624 5.624 0 0 1 0 4.114 5.358 5.358 0 0 1-1.172 1.806L12 21z\"></path>", '0 0 24 24', 14);
};
var definePopup$4 = function (_a) {
    var hasHeartIcon = _a.hasHeartIcon, label = _a.label;
    return "<div class=\"uitk-marker-popup-anchor price-popup-anchor " + (hasHeartIcon ? "heart-" + hasHeartIcon : '') + "\">\n    <div class=\"uitk-marker-popup-content uitk-marker-popup-content-price\">\n    <div class=\"marker-content\">\n      <span class=\"marker-content-label " + (hasHeartIcon ? 'heart-separator' : '') + "\">" + label + "</span>\n      " + (hasHeartIcon
        ? "<span class=\"uitk-marker-saved\">" + defineHeartSvg({
            hasHeartIcon: hasHeartIcon
        }) + "</span>"
        : '') + "\n    </div>\n    </div>\n</div>";
};
var PriceMarker = /** @class */ (function (_super) {
    __extends(PriceMarker, _super);
    function PriceMarker(_a) {
        var label = _a.label, props = __rest(_a, ["label"]);
        var _this = _super.call(this, props) || this;
        var hasHeartIcon = _this.props.hasHeartIcon;
        _this.popup.anchor.setAttribute('type', 'price');
        _this.popup.anchor.innerHTML = definePopup$4({ hasHeartIcon: hasHeartIcon, label: label });
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.label = {
            text: label
        };
        _this.marker.icon = defineHeartSvg({ hasHeartIcon: hasHeartIcon });
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        _this.marker.label = _this.label;
        return _this;
    }
    PriceMarker.prototype.getPosition = function () {
        return this.position;
    };
    PriceMarker.prototype.focus = function (props) {
        _super.prototype.focus.call(this, props);
    };
    // @ts-ignore
    PriceMarker.prototype.blur = function (props) {
        // @ts-ignore
        _super.prototype.blur.call(this, props);
    };
    return PriceMarker;
}(Marker$1));

var defineSvg$2 = function () {
    return defineMarkerSvg(16, "<path d=\"M 13.4333 6.76664 C 12.3733 7.82664 10.94 8.15997 9.91999 7.68664 L 8.93999 8.66664 L 13.5267 13.2533 L 12.5867 14.1933 L 7.99999 9.60664 L 3.40666 14.1866 L 2.46666 13.2466 L 8.97333 6.73997 C 8.49999 5.71997 8.83333 4.28664 9.89333 3.22664 C 11.1733 1.95331 13 1.7133 13.9733 2.68664 C 14.9533 3.66664 14.7067 5.4933 13.4333 6.76664 Z M 7.28666 7.00664 L 5.39999 8.89331 L 2.60666 6.10664 C 1.56666 5.05997 1.56666 3.3733 2.60666 2.3333 L 7.28666 7.00664 Z\"/>", '0 0 16 16', 16);
};
var definePopup$3 = function () { return defineLocationPopup(defineSvg$2()); };
var RestaurantMarker = /** @class */ (function (_super) {
    __extends(RestaurantMarker, _super);
    function RestaurantMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.innerHTML = definePopup$3();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg$2();
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    RestaurantMarker.prototype.getPosition = function () {
        return this.position;
    };
    return RestaurantMarker;
}(Marker$1));

var soldOutSvg = function (_a) {
    var _b = _a.hasHeartIcon, hasHeartIcon = _b === void 0 ? false : _b;
    return defineMarkerSvg(13, "<path d=\"M12 21l-8.438-8.689a5.358 5.358 0 0 1-1.171-1.806 5.624 5.624 0 0 1 0-4.114c.26-.669.65-1.27 1.171-1.806a5.126 5.126 0 0 1 1.739-1.184A5.174 5.174 0 0 1 7.283 3c.67 0 1.332.134 1.983.401.65.268 1.23.663 1.738 1.184L12 5.61l.996-1.024a5.126 5.126 0 0 1 1.738-1.184A5.174 5.174 0 0 1 16.717 3c.67 0 1.331.134 1.982.401.651.268 1.23.663 1.738 1.184.521.535.912 1.137 1.172 1.806a5.624 5.624 0 0 1 0 4.114 5.358 5.358 0 0 1-1.172 1.806L12 21z\"></path>", '0 0 24 24', 13, !hasHeartIcon);
};
var definePopup$2 = function (_a) {
    var hasHeartIcon = _a.hasHeartIcon;
    return "<div class=\"uitk-marker-popup-anchor soldout-popup-anchor\">\n  <div class=\"uitk-marker-popup-content uitk-marker-popup-content-soldout\">\n  " + (hasHeartIcon
        ? "<span class=\"uitk-marker-saved\">" + soldOutSvg({ hasHeartIcon: hasHeartIcon }) + "</span>"
        : "" + soldOutSvg({ hasHeartIcon: hasHeartIcon })) + "\n  </div>\n</div>";
};
var SoldOutMarker = /** @class */ (function (_super) {
    __extends(SoldOutMarker, _super);
    function SoldOutMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        var hasHeartIcon = _this.props.hasHeartIcon;
        _this.popup.anchor.setAttribute('type', 'soldOut');
        _this.popup.anchor.innerHTML = definePopup$2({ hasHeartIcon: hasHeartIcon });
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = soldOutSvg({ hasHeartIcon: hasHeartIcon });
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    SoldOutMarker.prototype.getPosition = function () {
        return this.position;
    };
    return SoldOutMarker;
}(Marker$1));

var defineSvg$1 = function () {
    return defineMarkerSvg(16, "<path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M7.99935 1.33337C5.33268 1.33337 2.66602 1.66671 2.66602 4.00004V10.3334C2.66602 11.62 3.71268 12.6667 4.99935 12.6667L3.99935 13.6667V14H5.48602L6.81935 12.6667H9.33268L10.666 14H11.9993V13.6667L10.9993 12.6667C12.286 12.6667 13.3327 11.62 13.3327 10.3334V4.00004C13.3327 1.66671 10.946 1.33337 7.99935 1.33337ZM4.99935 11.3334C4.44602 11.3334 3.99935 10.8867 3.99935 10.3334C3.99935 9.78004 4.44602 9.33337 4.99935 9.33337C5.55268 9.33337 5.99935 9.78004 5.99935 10.3334C5.99935 10.8867 5.55268 11.3334 4.99935 11.3334ZM3.99935 6.66671H7.33268V4.00004H3.99935V6.66671ZM8.66602 6.66671V4.00004H11.9993V6.66671H8.66602ZM9.99935 10.3334C9.99935 10.8867 10.446 11.3334 10.9993 11.3334C11.5527 11.3334 11.9993 10.8867 11.9993 10.3334C11.9993 9.78004 11.5527 9.33337 10.9993 9.33337C10.446 9.33337 9.99935 9.78004 9.99935 10.3334Z\"/>", '0 0 16 16', 16);
};
var definePopup$1 = function () { return defineLocationPopup(defineSvg$1()); };
var TransitMarker = /** @class */ (function (_super) {
    __extends(TransitMarker, _super);
    function TransitMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.innerHTML = definePopup$1();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg$1;
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    TransitMarker.prototype.getPosition = function () {
        return this.position;
    };
    return TransitMarker;
}(Marker$1));

var defineShape = function (props, position) {
    return new props.google.maps.Circle({
        center: position,
        fillColor: '#005AEB',
        fillOpacity: 0.36,
        radius: 500,
        strokeColor: '#FFF',
        strokeOpacity: 1,
        strokeWeight: 2,
        title: 'Obfuscation'
    });
};
var CircleMarker = /** @class */ (function (_super) {
    __extends(CircleMarker, _super);
    function CircleMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup = defineShape(props, _this.position);
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        return _this;
    }
    CircleMarker.prototype.getPosition = function () {
        return this.position;
    };
    return CircleMarker;
}(Marker$1));

var defineSvg = function () {
    return defineMarkerSvg(20, "<path d=\"M20.877 11.627l-1.494-1.389-1.494-4.974a.35.35 0 0 0-.132-.194.364.364 0 0 0-.22-.07H6.463a.364.364 0 0 0-.22.07.352.352 0 0 0-.132.176l-1.617 4.992-1.389 1.389a.342.342 0 0 0-.079.123.398.398 0 0 0-.026.14v4.448c0 .047.009.094.026.14a.342.342 0 0 0 .08.124l.65.632v2.127c0 .106.035.197.105.273.07.076.159.114.264.114h3.006a.344.344 0 0 0 .264-.114.388.388 0 0 0 .105-.273v-2.127h9v2.127c0 .106.035.197.105.273.07.076.159.114.264.114h3.006a.344.344 0 0 0 .264-.114.388.388 0 0 0 .105-.273v-2.127l.65-.632a.342.342 0 0 0 .08-.123.398.398 0 0 0 .026-.141v-4.43a.349.349 0 0 0-.035-.158.47.47 0 0 0-.088-.123zM7.307 6.494h9.386l1.125 3.744H6.182l1.125-3.744zm-.809 8.244c-.41 0-.762-.146-1.055-.44a1.438 1.438 0 0 1-.44-1.054c0-.41.147-.762.44-1.055.293-.293.645-.439 1.055-.439.41 0 .762.146 1.055.44.293.292.44.644.44 1.054 0 .41-.147.762-.44 1.055-.293.293-.645.44-1.055.44zm11.004 0c-.41 0-.762-.146-1.055-.44a1.438 1.438 0 0 1-.44-1.054c0-.41.147-.762.44-1.055.293-.293.645-.439 1.055-.439.41 0 .762.146 1.055.44.293.292.439.644.439 1.054 0 .41-.146.762-.44 1.055-.292.293-.644.44-1.054.44z\"/>", '0 0 24 24', 20);
};
var definePopup = function () {
    return "<div class=\"uitk-marker-popup-anchor\">\n    <div class=\"uitk-marker-popup-content uitk-marker-popup-content-car\">\n      " + defineSvg() + "\n    </div>\n  </div>";
};
var CarMarker = /** @class */ (function (_super) {
    __extends(CarMarker, _super);
    function CarMarker(_a) {
        var props = __rest(_a, []);
        var _this = _super.call(this, props) || this;
        _this.popup.anchor.setAttribute('type', 'car');
        _this.popup.anchor.innerHTML = definePopup();
        _this.popup.setMap(_this.map);
        _this.marker = _this.popup;
        _this.marker.getPosition = _this.getPosition.bind(_this);
        _this.marker.icon = defineSvg();
        _this.marker.map = _this.map;
        _this.marker.position = _this.position;
        return _this;
    }
    CarMarker.prototype.getPosition = function () {
        return this.position;
    };
    return CarMarker;
}(Marker$1));

var Marker = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Marker: Marker$1,
  defineMarkerSvg: defineMarkerSvg,
  defineLocationPopup: defineLocationPopup,
  AirportMarker: AirportMarker,
  HotelMarker: HotelMarker,
  PlaceMarker: PlaceMarker,
  PriceMarker: PriceMarker,
  RestaurantMarker: RestaurantMarker,
  SoldOutMarker: SoldOutMarker,
  TransitMarker: TransitMarker,
  CircleMarker: CircleMarker,
  CarMarker: CarMarker
});

var UitkMarker = /** @class */ (function (_super) {
    __extends(UitkMarker, _super);
    function UitkMarker(props) {
        var _this = _super.call(this, props) || this;
        _this.setMarkerFromProps(props);
        return _this;
    }
    UitkMarker.prototype.componentDidUpdate = function () {
        // Whenever the componentUpdated, call setMarkerFromProps() with currentProps
        this.setMarkerFromProps(this.props);
    };
    UitkMarker.prototype.componentWillUnmount = function () {
        if (this.marker && this.marker.marker) {
            this.marker.marker.setMap(null);
        }
    };
    UitkMarker.prototype.setMarkerFromProps = function (props) {
        var map = props.map, google = props.google, type = props.type;
        // Remove old marker from the map
        if (this.marker && this.marker.marker) {
            this.marker.marker.setMap(null);
        }
        if (google && map) {
            this.marker = new Marker[type](props);
        }
    };
    UitkMarker.prototype.render = function () {
        return null;
    };
    UitkMarker.displayName = 'UitkMarker';
    return UitkMarker;
}(React.PureComponent));

var constants = {
    type_property: 'property',
    status_available: 'available',
    status_filtered: 'filtered',
    status_unavailable: 'sold_out',
    status_unknown: 'unknown',
    cfg_markers: 'markers',
    cfg_show: 'show',
    marker_type: 'markerType',
    marker_type_price: 'price',
    type_place: 'place',
    marker_type_text: 'text',
    marker_action_on_mouse_over: 'actionOnMouseOver',
    marker_action_on_mouse_over_open_popup: 'openPopup',
    default_value: 'default',
    marker_action_on_mouse_over_send_event: 'sendEvent',
    threshold: 'threshold',
    clustering_type: 'type',
    clustering_type_supercluster: 'supercluster',
    clustering: 'clustering',
    clustering_supercluster: 'superclusterSettings',
    clustering_supercluster_radius: 'radius',
    clustering_excluded_qualifiers: 'excludedQualifiers',
    clustering_max_zoom: 'maxZoom',
    clustering_min_points: 'minPoints',
    marker_type_pin: 'pin',
    icons: 'icons',
    additional_css_class: 'additionalCssClass',
    cfg_iso_4217_currency_code: 'iso4217CurrencyCode',
    cfg_locale: 'locale',
    mark_highlighted: 'hl',
    type_cluster: 'cluster',
    marker_action_on_click: 'actionOnClick',
    marker_action_on_click_select: 'select',
    marker_action_on_click_zoom_to_extent: 'zoomToExtent',
    zIndex: 'zIndex',
    marker_action_on_mouse_out: 'actionOnMouseOut',
    marker_action_on_mouse_out_send_event: 'sendEvent',
    marker_type_obfuscate: 'obfuscate',
    marker_type_obfuscate_pin: 'pinObfuscate',
    marker_type_obfuscate_price: 'priceObfuscate',
    obfuscate_markers: 'obfuscateMarkers',
    radius_in_meters: 'radiusInM',
    min_zoom_for_display: 'minZoomForDisplay',
    google_maps_options: 'googleMaps',
    google_maps_additional_config: 'gmapsAddConfig',
    cfg_i18n: 'i18n',
    format_function: 'formatFunction',
    messages: 'messages',
    property_cluster_label_with_price: 'propClusterLblWPrice',
    mapprovider_mapbox: 'mapbox',
    mapprovider_google_maps: 'google-maps',
    mapbox_style_streets: 'mapbox://styles/mapbox/streets-v11',
    map_urls: 'urls',
    api: 'api',
    css: 'css',
    mapbox_options: 'mapbox',
    pluginUrls: 'pluginUrls',
    mark_action_replace: 'replace',
    mark_action_merge: 'merge'
};

var UitkEGMapsMarkerAction;
(function (UitkEGMapsMarkerAction) {
    UitkEGMapsMarkerAction["REPLACE"] = "replace";
    UitkEGMapsMarkerAction["MERGE"] = "merge";
})(UitkEGMapsMarkerAction || (UitkEGMapsMarkerAction = {}));
var UitkEGMapsMarkerType;
(function (UitkEGMapsMarkerType) {
    UitkEGMapsMarkerType["PROPERTY"] = "property";
    UitkEGMapsMarkerType["PLACE"] = "place";
    UitkEGMapsMarkerType["CLUSTER"] = "cluster";
})(UitkEGMapsMarkerType || (UitkEGMapsMarkerType = {}));
var UitkEGMapsRouteAction;
(function (UitkEGMapsRouteAction) {
    UitkEGMapsRouteAction["SHOW_ROUTES"] = "showRoutes";
    UitkEGMapsRouteAction["HIDE_ROUTES"] = "hideRoutes";
})(UitkEGMapsRouteAction || (UitkEGMapsRouteAction = {}));
var UitkEGMapsPropertyStatus;
(function (UitkEGMapsPropertyStatus) {
    UitkEGMapsPropertyStatus["UNKNOWN"] = "unknown";
    UitkEGMapsPropertyStatus["AVAILABLE"] = "available";
    UitkEGMapsPropertyStatus["SOLD_OUT"] = "sold_out";
    UitkEGMapsPropertyStatus["FILTERED"] = "filtered";
})(UitkEGMapsPropertyStatus || (UitkEGMapsPropertyStatus = {}));
var UitkEGMapsPlaceType;
(function (UitkEGMapsPlaceType) {
    UitkEGMapsPlaceType["LANDMARK"] = "landmark";
    UitkEGMapsPlaceType["RESTAURANT"] = "restaurant";
    UitkEGMapsPlaceType["GROCERY"] = "grocery";
    UitkEGMapsPlaceType["TRANSPORTATION"] = "transportation";
})(UitkEGMapsPlaceType || (UitkEGMapsPlaceType = {}));
// Qualifier names - they should be renamed to UitkEGMapsQualifierName, but remain as is for now for backward compatibility
var UitkEGMapsIconType;
(function (UitkEGMapsIconType) {
    UitkEGMapsIconType["SAVED_AVAILABLE"] = "saved available";
    UitkEGMapsIconType["SAVED_SOLD_OUT"] = "saved sold_out";
    UitkEGMapsIconType["SAVED"] = "saved";
    UitkEGMapsIconType["SAVED_VACATION_RENTAL"] = "saved vacation_rental";
    UitkEGMapsIconType["SOLD_OUT"] = "sold_out";
    UitkEGMapsIconType["OBFUSCATED"] = "obfuscated";
    UitkEGMapsIconType["OBFUSCATED_PIN"] = "obfuscated_pin";
    UitkEGMapsIconType["OBFUSCATED_PRICE"] = "obfuscated_price";
    UitkEGMapsIconType["AVAILABLE"] = "available";
    UitkEGMapsIconType["AIRPORT"] = "transport__airport";
    UitkEGMapsIconType["RESTAURANT"] = "service__restaurant";
    UitkEGMapsIconType["HOTEL"] = "lob_hotels";
    UitkEGMapsIconType["FOCAL_HOTEL"] = "focal lob_hotels";
    UitkEGMapsIconType["FOCAL"] = "focal";
    UitkEGMapsIconType["CAR"] = "lob_cars";
    UitkEGMapsIconType["CRUISE_TEXT"] = "portOfCall";
    UitkEGMapsIconType["TRANSIT"] = "transport__metroStation";
    UitkEGMapsIconType["FLIGHTPATH"] = "flightPath";
    UitkEGMapsIconType["GROCERY"] = "service__groceryStore";
    UitkEGMapsIconType["TRANSPORTATION"] = "transport__trainStation";
    UitkEGMapsIconType["PEOPLE"] = "activities__redeemingPoint";
    UitkEGMapsIconType["ACTIVITY"] = "activities__activity";
    UitkEGMapsIconType["PARKING"] = "service__parking";
    UitkEGMapsIconType["METRO_STATION"] = "transport__metroStation";
    UitkEGMapsIconType["BUS_STATION"] = "transport__busStation";
    UitkEGMapsIconType["LANDMARK"] = "placeOfInterest__landmark";
    UitkEGMapsIconType["POINT_OF_INTEREST"] = "tourism__placeOfInterest";
})(UitkEGMapsIconType || (UitkEGMapsIconType = {}));
// These should be renamed to UitkEGMapsIconType once the above enum is changed.
var UitkEGMapsUitkIconType;
(function (UitkEGMapsUitkIconType) {
    UitkEGMapsUitkIconType["AIRPORT"] = "lob_flights";
    UitkEGMapsUitkIconType["RESTAURANT"] = "local_dining";
    UitkEGMapsUitkIconType["HOTEL"] = "lob_hotels";
    UitkEGMapsUitkIconType["CAR"] = "lob_cars";
    UitkEGMapsUitkIconType["TRANSIT"] = "train";
    UitkEGMapsUitkIconType["FLIGHTPATH"] = "flightPath";
    UitkEGMapsUitkIconType["LANDMARK"] = "place";
    UitkEGMapsUitkIconType["GROCERY"] = "shopping_cart";
    UitkEGMapsUitkIconType["TRANSPORTATION"] = "train";
    UitkEGMapsUitkIconType["PEOPLE"] = "people";
    UitkEGMapsUitkIconType["ACTIVITY"] = "lob_activities";
    UitkEGMapsUitkIconType["PARKING"] = "local_parking";
    UitkEGMapsUitkIconType["METRO_STATION"] = "train";
    UitkEGMapsUitkIconType["BUS_STATION"] = "directions_bus";
    UitkEGMapsUitkIconType["POI"] = "circle_filled";
    UitkEGMapsUitkIconType["SOLD_OUT"] = "remove";
    UitkEGMapsUitkIconType["VACATION_RENTAL"] = "lob_vacation_rentals";
})(UitkEGMapsUitkIconType || (UitkEGMapsUitkIconType = {}));

var UitkEGMapsType;
(function (UitkEGMapsType) {
    UitkEGMapsType["MAPBOX"] = "mapbox";
    UitkEGMapsType["GOOGLE"] = "google";
})(UitkEGMapsType || (UitkEGMapsType = {}));
/*
  This is a helper function to convert the center coordinates from event to EGMapsCoordinates object
*/
var getCenter = function (event) {
    var _a, _b;
    var center = {
        lat: (_a = event === null || event === void 0 ? void 0 : event.bounds) === null || _a === void 0 ? void 0 : _a.center[0],
        lng: (_b = event === null || event === void 0 ? void 0 : event.bounds) === null || _b === void 0 ? void 0 : _b.center[1]
    };
    return center;
};
/*
  This is a helper function to convert the bounds from event to EGMapsBounds object
*/
var getBounds = function (event) {
    var _a, _b, _c, _d;
    var ne = {
        lat: (_a = event === null || event === void 0 ? void 0 : event.bounds) === null || _a === void 0 ? void 0 : _a.ne[0],
        lng: (_b = event === null || event === void 0 ? void 0 : event.bounds) === null || _b === void 0 ? void 0 : _b.ne[1]
    };
    var sw = {
        lat: (_c = event === null || event === void 0 ? void 0 : event.bounds) === null || _c === void 0 ? void 0 : _c.sw[0],
        lng: (_d = event === null || event === void 0 ? void 0 : event.bounds) === null || _d === void 0 ? void 0 : _d.sw[1]
    };
    var bounds = {
        ne: ne,
        sw: sw
    };
    return bounds;
};
/*
  This is a helper function that calls the onMapBoundsChanged function that is passed by the user with the bounds change event and changes
  the map's viewport.
*/
var onMapBoundsChanged = function (onZoomIn, onZoomOut, onMapPan, mapObj) { return function (event) {
    if ((event === null || event === void 0 ? void 0 : event.originalEvent) === 'zoomIn' && !!onZoomIn) {
        onZoomIn(getCenter(event), getBounds(event), mapObj === null || mapObj === void 0 ? void 0 : mapObj.getZoom());
    }
    if ((event === null || event === void 0 ? void 0 : event.originalEvent) === 'zoomOut' && !!onZoomOut) {
        onZoomOut(getCenter(event), getBounds(event), mapObj === null || mapObj === void 0 ? void 0 : mapObj.getZoom());
    }
    if ((event === null || event === void 0 ? void 0 : event.originalEvent) === 'drag' && !!onMapPan) {
        onMapPan(getCenter(event), getBounds(event), mapObj === null || mapObj === void 0 ? void 0 : mapObj.getZoom());
    }
}; };
/*
  This is a helper function that calls the onMapHoverChanged function that is passed by the user with the hover change event.
*/
var onMapHoverChanged = function (onHoverChange) { return function (event) {
    if (onHoverChange) {
        onHoverChange(event === null || event === void 0 ? void 0 : event.feature, event === null || event === void 0 ? void 0 : event.setPopupContent, event === null || event === void 0 ? void 0 : event.action);
    }
}; };
/*
  This is a helper function that calls the onSelectionChange function that is passed by the user with the selection change event.
*/
var onMapSelectionChange = function (onClusterSelect, onMarkerSelect, onMarkerDeselect) { return function (event) {
    var _a;
    if ((event === null || event === void 0 ? void 0 : event.action) === 'select') {
        if (((_a = event === null || event === void 0 ? void 0 : event.feature) === null || _a === void 0 ? void 0 : _a.type) === 'cluster' && !!onClusterSelect) {
            onClusterSelect(event === null || event === void 0 ? void 0 : event.feature, event === null || event === void 0 ? void 0 : event.setPopupContent, event === null || event === void 0 ? void 0 : event.action);
        }
        else if (onMarkerSelect) {
            onMarkerSelect(event === null || event === void 0 ? void 0 : event.feature, event === null || event === void 0 ? void 0 : event.setPopupContent, event === null || event === void 0 ? void 0 : event.action);
        }
    }
    else if ((event === null || event === void 0 ? void 0 : event.action) === 'clearSelection' && !!onMarkerDeselect) {
        onMarkerDeselect(event === null || event === void 0 ? void 0 : event.feature, event === null || event === void 0 ? void 0 : event.setPopupContent, event === null || event === void 0 ? void 0 : event.action);
    }
}; };
/*
  This is a helper function that calls the onMarkerChange function that is passed by the user with the marker change event.
*/
var onMapMarkerChange = function (onMarkerChange) { return function (event) {
    if (onMarkerChange) {
        onMarkerChange(event);
    }
}; };
/*
  This is a helper function that modifies the marker object to a structure that core eg-maps library supports.
*/
var modifyMarkerObj = function (markers) {
    var replacements = { text: 'price', markerText: 'text' };
    var modifiedMarkers = [];
    // eslint-disable-next-line array-callback-return
    markers.map(function (marker) {
        var replacedItems = Object.keys(marker).map(function (key) {
            var _a;
            var newKey = replacements[key] || key;
            return _a = {}, _a[newKey] = marker[key], _a;
        });
        // @ts-ignore
        modifiedMarkers.push(replacedItems.reduce(function () { return Object.assign.apply(Object, __spreadArray([{}], replacedItems, false)); }));
    });
    return modifiedMarkers;
};
var markersChanged = function (markers, mapObj) {
    var currentMarkers = mapObj.listFeatures().filter(function (m) { return m.type !== 'cluster'; });
    if ((currentMarkers === null || currentMarkers === void 0 ? void 0 : currentMarkers.length) === (markers === null || markers === void 0 ? void 0 : markers.length)) {
        return currentMarkers.some(function (m) { return !(markers === null || markers === void 0 ? void 0 : markers.find(function (mm) { return mm.id === m.id && mm.type === m.type; })); });
    }
    return true;
};
/*
  This is a helper function that renders the markers on the map and changes the map's viewport to display the properties.
*/
var renderMarkers = function (markers, mapObj, mapConfig, destinations) {
    if (destinations === void 0) { destinations = []; }
    return __awaiter(void 0, void 0, void 0, function () {
        var _a, markerAction, _b, autoFitBounds, modifiedMarkers, destinationMarkers, allMarkers, bounds, destinationList;
        return __generator(this, function (_c) {
            _a = mapConfig.markerAction, markerAction = _a === void 0 ? UitkEGMapsMarkerAction.REPLACE : _a, _b = mapConfig.autoFitBounds, autoFitBounds = _b === void 0 ? true : _b;
            modifiedMarkers = modifyMarkerObj(markers);
            destinationMarkers = destinations && destinations.length > 0
                ? modifyMarkerObj(destinations.map(function (d) { return d.marker; }).filter(function (item, pos, self) { return self.indexOf(item) === pos; }))
                : [];
            allMarkers = __spreadArray(__spreadArray([], modifiedMarkers, true), destinationMarkers, true);
            bounds = mapObj.pushFeatureData(allMarkers, markerAction === UitkEGMapsMarkerAction.REPLACE ? constants.mark_action_replace : constants.mark_action_merge);
            if (bounds && autoFitBounds && markersChanged(allMarkers, mapObj))
                mapObj.fitBounds(bounds.sw, bounds.ne);
            if (destinations && destinations.length > 0) {
                destinationList = destinations.map(function (d) { return ({
                    idAndType: { id: d.marker.id, type: d.marker.type },
                    transportMode: EGDistances.modes[d.mode]
                }); });
                mapObj.registerRouteDestination.apply(mapObj, destinationList);
            }
            return [2 /*return*/];
        });
    });
};
/*
  This is a helper function that calls the onTilesLoaded event
*/
var onMapTilesLoad = function (onTilesLoad) { return function (event) {
    if (onTilesLoad) {
        onTilesLoad(event);
    }
}; };
/*
  This is a helper function that changes the map's viewport to the passed lat/lng and also zooms in to the passed value.
*/
var centerAndZoom = function (latitude, longitude, zoom, mapObj) {
    if (mapObj && latitude && longitude) {
        mapObj.centerAndZoomMap([latitude, longitude], zoom);
    }
};
/*
  This is a helper function that changes the map's viewport according to the lat/lng passed in the bounding box.
*/
var fitBounds = function (bbox, mapObj) {
    if (mapObj && bbox && bbox.length > 0) {
        mapObj.fitBounds(bbox[0], bbox[1]);
    }
};
/*
  This is a helper function that calls the onTilesLoaded event
*/
var onMapIdle = function (onIdle) { return function (event) {
    if (onIdle) {
        onIdle(event);
    }
}; };
var onRouteChange = function (onRouteSelect, onRouteDeselect) { return function (event) {
    if ((event === null || event === void 0 ? void 0 : event.action) === 'show' && !!onRouteSelect) {
        onRouteSelect(event === null || event === void 0 ? void 0 : event.routes, event === null || event === void 0 ? void 0 : event.action);
    }
    else if ((event === null || event === void 0 ? void 0 : event.action) === 'hide' && !!onRouteDeselect) {
        onRouteDeselect(event === null || event === void 0 ? void 0 : event.routes, event === null || event === void 0 ? void 0 : event.action);
    }
}; };

/* eslint-disable react-hooks/exhaustive-deps */
var concatPromises = function (urls) {
    var promises = [];
    var head = document.querySelector('head');
    urls.forEach(function (url) {
        var promise = new Promise(function (res) {
            var script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.defer = true;
            script.onload = function () {
                res();
            };
            script.onerror = function () {
                console.error("An error occured while loading " + url);
                res();
            };
            if (head) {
                head.appendChild(script);
            }
        });
        promises.push(promise);
    });
    return promises;
};
var initMapBoxApi = function (mapBoxJsUrl, mapboxplugins, mapBoxCssUrl) {
    if (window.mapboxgl) {
        return Promise.resolve();
    }
    return new Promise(function (resolve) {
        var head = document.querySelector('head');
        var urls = [mapBoxJsUrl, mapboxplugins];
        Promise.all(concatPromises(urls)).then(function () { return resolve(); });
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = mapBoxCssUrl;
        if (head) {
            head.appendChild(link);
        }
    });
};
var useIsMounted = function () {
    var isMounted = React.useRef(false);
    React.useEffect(function () {
        isMounted.current = true;
        return function () {
            isMounted.current = false;
        };
    }, []);
    return isMounted;
};
var UitkEGMapsScriptLoader = function (_a) {
    var mapType = _a.mapType, accessToken = _a.accessToken, children = _a.children, locale = _a.locale, googleMapsId = _a.googleMapsId;
    var isMounted = useIsMounted();
    var _b = React.useState(1), loadingCount = _b[0], setLoadingCount = _b[1];
    var egMapsConfig = defaultConfig();
    var google_maps_options = constants.google_maps_options, map_urls = constants.map_urls, api = constants.api, css = constants.css, mapbox_options = constants.mapbox_options, pluginUrls = constants.pluginUrls;
    var googleUrl = egMapsConfig[google_maps_options][map_urls][api] + "&key=" + accessToken + "&language=" + locale;
    if (googleMapsId) {
        googleUrl += "&map_ids=" + googleMapsId;
    }
    var mapBoxJsUrl = egMapsConfig[mapbox_options][map_urls][api];
    var mapBoxCssUrl = egMapsConfig[mapbox_options][map_urls][css];
    var mapboxplugins = egMapsConfig[mapbox_options][map_urls][pluginUrls];
    React.useEffect(function () {
        if (mapType === UitkEGMapsType.GOOGLE) {
            ScriptLoader.getScript(googleUrl)
                .then(function () {
                if (isMounted.current) {
                    setLoadingCount(0);
                }
            })
                .catch(function (err) {
                console.error(err);
            });
        }
        else {
            initMapBoxApi(mapBoxJsUrl, mapboxplugins, mapBoxCssUrl)
                .then(function () {
                if (isMounted.current) {
                    setLoadingCount(0);
                }
            })
                .catch(function (err) {
                console.error(err);
            });
        }
    }, []);
    return (React.createElement(React.Fragment, null,
        loadingCount === 0 && children,
        loadingCount === 1 && (React.createElement("p", { className: "uitk-map-viewer-loading" },
            React.createElement(LocalizedText, { message: "loading" })))));
};

/*
  This is a HOC that loads the google/mapbox JS api before rendering the child component that is passed as a parameter.
*/
var withScriptLoader = function (childComponent, props) {
    var _a, _b;
    var scriptLoaderProps = {
        accessToken: props.accessToken,
        googleMapsId: (_a = props.mapConfig) === null || _a === void 0 ? void 0 : _a.googleMapsId,
        locale: (_b = props.mapConfig) === null || _b === void 0 ? void 0 : _b.locale,
        mapType: props.mapType
    };
    return React.createElement(UitkEGMapsScriptLoader, __assign({}, scriptLoaderProps), childComponent);
};
/*
  This function takes icon name and brand as a parameter and renders the svg into a div
*/
var buildIcon = function (iconName, brand) {
    var icon = (React.createElement(BrandProvider, { config: brand },
        React.createElement("div", { className: "uitk-eg-maps-icon-container" },
            React.createElement(UitkIcon, { name: iconName, className: "uitk-eg-maps-primary-icon " + iconName }))));
    var container = document.createElement('div');
    ReactDOM.render(icon, container);
    return container;
};

var type_property = constants.type_property, status_unavailable = constants.status_unavailable, cfg_markers = constants.cfg_markers, cfg_show = constants.cfg_show, marker_type = constants.marker_type, marker_type_text = constants.marker_type_text, type_place = constants.type_place, default_value = constants.default_value, clustering_type = constants.clustering_type, clustering_type_supercluster = constants.clustering_type_supercluster, clustering = constants.clustering, clustering_supercluster = constants.clustering_supercluster, clustering_supercluster_radius = constants.clustering_supercluster_radius, clustering_excluded_qualifiers = constants.clustering_excluded_qualifiers, clustering_max_zoom = constants.clustering_max_zoom, clustering_min_points = constants.clustering_min_points, marker_type_pin = constants.marker_type_pin, icons = constants.icons, mark_highlighted = constants.mark_highlighted, marker_type_obfuscate = constants.marker_type_obfuscate, marker_type_obfuscate_pin = constants.marker_type_obfuscate_pin, marker_type_obfuscate_price = constants.marker_type_obfuscate_price, obfuscate_markers = constants.obfuscate_markers, radius_in_meters = constants.radius_in_meters, min_zoom_for_display = constants.min_zoom_for_display, threshold = constants.threshold, cfg_i18n = constants.cfg_i18n, messages = constants.messages, property_cluster_label_with_price = constants.property_cluster_label_with_price;
var defaultEGMapsConfig = function (brand, getCssValue) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    var routeColor = getCssValue('--map__route__border_color');
    var routeSize = parseInt(getCssValue('border-width'), 10);
    var mapConfig = (_a = {},
        _a[cfg_markers] = (_b = {},
            _b[type_property] = (_c = {},
                _c[cfg_show] = (_d = {},
                    _d[default_value] = true,
                    _d[UitkEGMapsPropertyStatus.AVAILABLE] = true,
                    _d[UitkEGMapsPropertyStatus.SOLD_OUT] = true,
                    _d[UitkEGMapsPropertyStatus.UNKNOWN] = true,
                    _d),
                _c[marker_type] = (_e = {},
                    _e[UitkEGMapsIconType.CRUISE_TEXT] = marker_type_text,
                    _e[status_unavailable] = marker_type_pin,
                    _e[UitkEGMapsIconType.OBFUSCATED] = marker_type_obfuscate,
                    _e[UitkEGMapsIconType.OBFUSCATED_PIN] = marker_type_obfuscate_pin,
                    _e[UitkEGMapsIconType.OBFUSCATED_PRICE] = marker_type_obfuscate_price,
                    _e[UitkEGMapsIconType.HOTEL] = marker_type_pin,
                    _e[UitkEGMapsIconType.FOCAL_HOTEL] = marker_type_pin,
                    _e[UitkEGMapsIconType.SAVED_VACATION_RENTAL] = marker_type_pin,
                    _e),
                _c[icons] = (_f = {},
                    _f["" + mark_highlighted] = null,
                    _f[UitkEGMapsIconType.SOLD_OUT] = buildIcon(UitkEGMapsUitkIconType.SOLD_OUT, brand),
                    _f[UitkEGMapsIconType.HOTEL] = buildIcon(UitkEGMapsUitkIconType.HOTEL, brand),
                    _f[UitkEGMapsIconType.FOCAL_HOTEL] = buildIcon(UitkEGMapsUitkIconType.HOTEL, brand),
                    _f[UitkEGMapsIconType.CAR] = buildIcon(UitkEGMapsUitkIconType.CAR, brand),
                    _f[UitkEGMapsIconType.ACTIVITY] = buildIcon(UitkEGMapsUitkIconType.ACTIVITY, brand),
                    _f[UitkEGMapsIconType.SAVED_VACATION_RENTAL] = buildIcon(UitkEGMapsUitkIconType.VACATION_RENTAL, brand),
                    _f[UitkEGMapsIconType.OBFUSCATED_PIN] = buildIcon(UitkEGMapsUitkIconType.HOTEL, brand),
                    _f),
                _c[min_zoom_for_display] = (_g = {},
                    _g[default_value] = -1,
                    _g),
                _c[clustering] = (_h = {},
                    _h[clustering_type] = clustering_type_supercluster,
                    _h[clustering_excluded_qualifiers] = [UitkEGMapsIconType.SOLD_OUT],
                    _h[clustering_supercluster] = (_j = {},
                        _j[clustering_supercluster_radius] = 105,
                        _j),
                    _h[clustering_max_zoom] = 15,
                    _h[clustering_min_points] = 10,
                    _h),
                _c[obfuscate_markers] = (_k = {
                        icons: {
                            default: 'https://a.travel-assets.com/travel-assets-manager/eg-maps/obfuscated.svg'
                        },
                        type: {
                            default: 'circle'
                        }
                    },
                    _k[radius_in_meters] = (_l = {},
                        _l[default_value] = 1000,
                        _l),
                    _k.style = {
                        default: {
                            line: {
                                weight: 1,
                                color: '#616161',
                                opacity: 1
                            },
                            fill: {
                                color: '#E6E6E6',
                                opacity: 0.5
                            }
                        },
                        hl: {
                            line: {
                                weight: 1,
                                color: '#616161',
                                opacity: 1
                            },
                            fill: {
                                color: '#E6E6E6',
                                opacity: 0.5
                            }
                        }
                    },
                    _k),
                _c),
            _b[type_place] = (_m = {},
                _m[cfg_show] = (_o = {},
                    _o[default_value] = true,
                    _o[UitkEGMapsPropertyStatus.UNKNOWN] = true,
                    _o[UitkEGMapsPlaceType.LANDMARK] = true,
                    _o[UitkEGMapsPlaceType.RESTAURANT] = true,
                    _o[UitkEGMapsPlaceType.GROCERY] = true,
                    _o[UitkEGMapsPlaceType.TRANSPORTATION] = true,
                    _o),
                _m[marker_type] = (_p = {},
                    _p[default_value] = marker_type_pin,
                    _p),
                _m[min_zoom_for_display] = (_q = {},
                    _q[default_value] = -1,
                    _q),
                _m[threshold] = (_r = {},
                    _r[default_value] = 1000,
                    _r),
                _m[icons] = (_s = {},
                    _s[default_value] = buildIcon(UitkEGMapsUitkIconType.POI, brand),
                    _s[UitkEGMapsPropertyStatus.UNKNOWN] = buildIcon(UitkEGMapsUitkIconType.POI, brand),
                    _s["" + mark_highlighted] = null,
                    _s[UitkEGMapsIconType.AIRPORT] = buildIcon(UitkEGMapsUitkIconType.AIRPORT, brand),
                    _s[UitkEGMapsIconType.RESTAURANT] = buildIcon(UitkEGMapsUitkIconType.RESTAURANT, brand),
                    _s[UitkEGMapsIconType.CAR] = buildIcon(UitkEGMapsUitkIconType.CAR, brand),
                    _s[UitkEGMapsIconType.TRANSIT] = buildIcon(UitkEGMapsUitkIconType.TRANSIT, brand),
                    _s[UitkEGMapsIconType.GROCERY] = buildIcon(UitkEGMapsUitkIconType.GROCERY, brand),
                    _s[UitkEGMapsIconType.TRANSPORTATION] = buildIcon(UitkEGMapsUitkIconType.TRANSPORTATION, brand),
                    _s[UitkEGMapsIconType.METRO_STATION] = buildIcon(UitkEGMapsUitkIconType.METRO_STATION, brand),
                    _s[UitkEGMapsIconType.BUS_STATION] = buildIcon(UitkEGMapsUitkIconType.BUS_STATION, brand),
                    _s[UitkEGMapsIconType.PARKING] = buildIcon(UitkEGMapsUitkIconType.PARKING, brand),
                    _s[UitkEGMapsIconType.ACTIVITY] = buildIcon(UitkEGMapsUitkIconType.ACTIVITY, brand),
                    _s[UitkEGMapsIconType.PEOPLE] = buildIcon(UitkEGMapsUitkIconType.PEOPLE, brand),
                    _s),
                _m),
            _b),
        _a.routes = {
            style: {
                driving: {
                    lineStyle: 'normal',
                    color: routeColor,
                    width: routeSize,
                    opacity: 1
                },
                cycling: {
                    lineStyle: 'normal',
                    color: routeColor,
                    width: routeSize,
                    opacity: 1
                },
                walking: {
                    lineStyle: 'normal',
                    color: routeColor,
                    width: routeSize,
                    opacity: 1
                },
                flying: {
                    color: routeColor,
                    opacity: 1
                }
            }
        },
        _a[cfg_i18n] = (_t = {},
            _t[messages] = (_u = {},
                _u[property_cluster_label_with_price] = '{0} properties',
                _u),
            _t),
        _a);
    return mapConfig;
};

var UitkEGMapsContext = createContext(null);

/* eslint-disable react-hooks/exhaustive-deps */
var google_maps_options = constants.google_maps_options, google_maps_additional_config = constants.google_maps_additional_config;
var UitkEGMapsContainer = function (_a) {
    var _b;
    var height = _a.height, width = _a.width, viewState = _a.viewState, accessToken = _a.accessToken, callbacks = _a.callbacks, mapStyle = _a.mapStyle, children = _a.children, mapType = _a.mapType, _c = _a.markers, markers = _c === void 0 ? [] : _c, destinations = _a.destinations, _d = _a.egDistancesEnabled, egDistancesEnabled = _d === void 0 ? false : _d, mapConfig = _a.mapConfig, _e = _a.useBoundsComputation, useBoundsComputation = _e === void 0 ? false : _e, boundsComputationConfiguration = _a.boundsComputationConfiguration, className = _a.className, innerRef = _a.innerRef;
    var mapprovider_mapbox = constants.mapprovider_mapbox, mapprovider_google_maps = constants.mapprovider_google_maps, mapbox_style_streets = constants.mapbox_style_streets;
    var mapProvider = mapType === UitkEGMapsType.GOOGLE ? mapprovider_google_maps : mapprovider_mapbox;
    var _f = useState({ mapObj: null, mapConfig: null }), map = _f[0], setMap = _f[1];
    var _g = callbacks || {}, onZoomIn = _g.onZoomIn, onZoomOut = _g.onZoomOut, onClusterSelect = _g.onClusterSelect, onMarkerSelect = _g.onMarkerSelect, onMarkerDeselect = _g.onMarkerDeselect, onMapPan = _g.onMapPan, onHoverChange = _g.onHoverChange, onMarkerChange = _g.onMarkerChange, onIdle = _g.onIdle, onTilesLoad = _g.onTilesLoad, onRouteSelect = _g.onRouteSelect, onRouteDeselect = _g.onRouteDeselect, beforeInit = _g.beforeInit, onInit = _g.onInit, afterRenderMarkers = _g.afterRenderMarkers;
    var _h = viewState || {}, longitude = _h.longitude, latitude = _h.latitude, zoom = _h.zoom, bbox = _h.bbox;
    var _j = mapConfig || {}, locale = _j.locale, autoFitBounds = _j.autoFitBounds, markerAction = _j.markerAction;
    var _k = useState(true), firstLoad = _k[0], setFirstLoad = _k[1];
    var containerHeight = height || '100%';
    var containerWidth = width || '100%';
    var mapRef = useRef();
    var brand = useBrand();
    var gmapCfg = function (mapId) {
        var _a, _b;
        var _c, _d, _e, _f;
        return (_a = {
                mapProvider: mapprovider_google_maps
            },
            _a[google_maps_options] = (_b = {},
                _b[google_maps_additional_config] = {
                    mapId: mapId,
                    streetViewControl: true,
                    streetViewControlOptions: {
                        position: (_d = (_c = window === null || window === void 0 ? void 0 : window.google) === null || _c === void 0 ? void 0 : _c.maps) === null || _d === void 0 ? void 0 : _d.ControlPosition.RIGHT_TOP
                    },
                    zoomControlOptions: {
                        position: (_f = (_e = window === null || window === void 0 ? void 0 : window.google) === null || _e === void 0 ? void 0 : _e.maps) === null || _f === void 0 ? void 0 : _f.ControlPosition.RIGHT_TOP
                    },
                    scaleControl: true
                },
                _b),
            _a);
    };
    var mapboxCfg = {
        mapProvider: mapprovider_mapbox,
        mapbox: {
            accessToken: accessToken,
            style: locale ? mapbox_style_streets : mapStyle
        }
    };
    var hasDestinations = destinations ? destinations.length > 0 : false;
    var egDistancesConfig;
    if (hasDestinations || egDistancesEnabled) {
        var egDistances = mapProvider === mapprovider_mapbox
            ? new EGDistances({
                provider: EGDistances.providers.mapbox,
                providers: (_b = {},
                    _b[EGDistances.providers.mapbox] = {
                        key: 'pk.eyJ1IjoiZXhwZWRpYS1nZW8iLCJhIjoiY2s3NTc2aTExMDJwZDNmbnN1cDdvcmRzNSJ9.Grb_k2_kcl7NXEu6paSB1w'
                    },
                    _b)
            })
            : new EGDistances({ provider: EGDistances.providers.google });
        egDistancesConfig = { routes: { egDistances: egDistances } };
    }
    useImperativeHandle(innerRef, function () {
        return ({
            map: function () { return map === null || map === void 0 ? void 0 : map.mapObj; },
            selectFeature: function (id, type, html) {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.selectFeature(id, type, html);
            },
            clearSelectedFeature: function () {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.clearSelectedFeature();
            },
            setPopupContent: function (id, type, html) {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.setPopupContent(id, type, html);
            },
            getBounds: function () {
                if (map && map.mapObj)
                    return map.mapObj.mapImpl.getBounds();
            },
            fitBounds: function (bounds) {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.fitBounds(bounds.sw, bounds.ne);
            },
            markFeatureOver: function (id, type) {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.markFeatureOver(id, type);
            },
            markFeatureOut: function (id, type) {
                if (map && map.mapObj)
                    map.mapObj.mapImpl.markFeatureOut(id, type);
            }
        });
    });
    var mapMemoValue = useMemo(function () { return ({ map: map, setMap: setMap }); }, [map, setMap]);
    var idSuffix = Math.floor(Math.random() * 90);
    var overridedBoundsParams = boundsComputationConfiguration !== null && boundsComputationConfiguration !== void 0 ? boundsComputationConfiguration : {};
    var initialViewport = function () {
        if (useBoundsComputation)
            return {
                coordinates: modifyMarkerObj(markers),
                useBoundsComputation: useBoundsComputation,
                options: overridedBoundsParams
            };
        if (latitude && longitude && zoom) {
            return {
                center: [latitude, longitude],
                zoom: zoom
            };
        }
        if (bbox) {
            var sw = bbox[0], ne = bbox[1];
            return { sw: sw, ne: ne };
        }
        return modifyMarkerObj(markers);
    };
    useEffect(function () {
        var config = mergeAndConcat(defaultEGMapsConfig(brand, function (cssPropertyName) {
            return getComputedStyle(mapRef.current).getPropertyValue(cssPropertyName);
        }), mapProvider === mapprovider_mapbox ? mapboxCfg : gmapCfg(mapConfig === null || mapConfig === void 0 ? void 0 : mapConfig.googleMapsId), egDistancesConfig !== null && egDistancesConfig !== void 0 ? egDistancesConfig : {}, (mapConfig === null || mapConfig === void 0 ? void 0 : mapConfig.egMapsConfig) || {});
        var mapObj = new EGMap(config);
        if (beforeInit)
            beforeInit(mapObj);
        mapObj.initMap(mapRef.current, true, initialViewport()).then(function () {
            mapObj.addBoundChangeListener(onMapBoundsChanged(onZoomIn, onZoomOut, onMapPan, mapObj));
            mapObj.addHoverChangeListener(onMapHoverChanged(onHoverChange));
            mapObj.addSelectionChangeListener(onMapSelectionChange(onClusterSelect, onMarkerSelect, onMarkerDeselect));
            mapObj.addMarkerChangeListener(onMapMarkerChange(onMarkerChange));
            if (hasDestinations)
                mapObj.addRouteChangeListener(onRouteChange(onRouteSelect, onRouteDeselect));
            mapObj.addIdleListener(onMapIdle(onIdle));
            mapObj.addTilesLoadedListener(onMapTilesLoad(onTilesLoad));
            setMap({ mapObj: mapObj, mapConfig: config });
            if (onInit)
                onInit(mapObj);
            setFirstLoad(false);
        });
        return function () {
            if (locale && window.google) {
                window.google = null;
            }
        };
    }, [mapProvider]);
    useEffect(function () {
        !firstLoad && !useBoundsComputation && centerAndZoom(latitude, longitude, zoom, map.mapObj);
    }, [map.mapObj, latitude, longitude, zoom]);
    useEffect(function () {
        !firstLoad && !useBoundsComputation && fitBounds(bbox, map.mapObj);
    }, [map.mapObj, bbox]);
    useEffect(function () {
        if (map.mapObj && markers && markers.length > 0) {
            renderMarkers(markers, map.mapObj, mapConfig || {}, destinations);
            if (afterRenderMarkers)
                afterRenderMarkers(map.mapObj);
        }
    }, [map.mapObj, markerAction, markers, autoFitBounds, destinations, afterRenderMarkers]);
    return (React.createElement("div", { className: classNames('uitk-eg-maps-container', 'uitk-eg-maps', className), style: { width: containerWidth, height: containerHeight }, id: "uitk-eg-maps-container-" + idSuffix, ref: mapRef }, map.mapObj && React.createElement(UitkEGMapsContext.Provider, { value: mapMemoValue }, children)));
};

/**
 * Business-level eg-maps component.
 *
 */
var UitkEGMaps = function (_a) {
    var children = _a.children, rest = __rest(_a, ["children"]);
    return withScriptLoader(React.createElement(UitkEGMapsContainer, __assign({}, rest), children), __assign({}, rest));
};
var defaultProps = {
    clustering: false,
    height: '100%',
    mapStyle: 'mapbox://styles/mapbox/streets-v10',
    mapType: UitkEGMapsType.GOOGLE,
    markerType: 'price',
    showPopup: false,
    width: '100%'
};
UitkEGMaps.defaultProps = defaultProps;

var UitkEGMapsDistanceMode;
(function (UitkEGMapsDistanceMode) {
    UitkEGMapsDistanceMode["cycling"] = "cycling";
    UitkEGMapsDistanceMode["walking"] = "walking";
    UitkEGMapsDistanceMode["driving"] = "driving";
    UitkEGMapsDistanceMode["flying"] = "flying";
})(UitkEGMapsDistanceMode || (UitkEGMapsDistanceMode = {}));

var defaultStaticMapsConfig = {
    mapProvider: 'google-maps',
    googleMaps: {
        mapStyle: '57df04e09ffe3110',
        locale: 'en_US'
    },
    staticMap: {
        positionMode: 'fitBounds'
    }
};
/**
 * Business-level static eg-maps component.
 *
 */
var UitkStaticEgMapsContainer = function (_a) {
    var staticMapConfig = _a.staticMapConfig, features = _a.features, routes = _a.routes, polygons = _a.polygons, 
    // eslint-disable-next-line
    _b = _a.getImage, 
    // eslint-disable-next-line
    getImage = _b === void 0 ? function () { } : _b, _c = _a.render, render = _c === void 0 ? true : _c;
    var mapRef = useRef();
    var idSuffix = Math.floor(Math.random() * 90);
    var _d = React.useState(), egDistances = _d[0], setEgDistances = _d[1];
    var _e = React.useState(), distanceProvider = _e[0], setDistanceProvider = _e[1];
    React.useEffect(function () {
        // @ts-ignore
        setEgDistances();
        setDistanceProvider(EGDistances.providers.google);
    }, []);
    React.useEffect(function () {
        var _a;
        if (routes && !egDistances && distanceProvider) {
            var config = {
                provider: distanceProvider,
                providers: (_a = {},
                    _a[EGDistances.providers.mapbox] = {
                        key: 'pk.eyJ1IjoiZXhwZWRpYS1nZW8iLCJhIjoiY2s3NTc2aTExMDJwZDNmbnN1cDdvcmRzNSJ9.Grb_k2_kcl7NXEu6paSB1w'
                    },
                    _a)
            };
            setEgDistances(new EGDistances(config));
        }
    }, [distanceProvider, egDistances, routes]);
    useEffect(function () {
        if (routes && !egDistances) {
            return;
        }
        var config = mergeAndConcat(defaultStaticMapsConfig, { routes: { egDistances: egDistances } }, staticMapConfig || {});
        var mapObj = new EGStaticMap(config, mapRef.current);
        mapObj
            .initMap(mapRef.current)
            .then(function () {
            mapObj.pushFeatureData(features);
            mapObj.pushRouteData(routes);
            mapObj.pushPolygonData(polygons);
            return mapObj.updateMap();
        })
            .then(function (imageSrc) {
            getImage(imageSrc);
        });
    }, [egDistances, routes, polygons, features, getImage, staticMapConfig]);
    return (React.createElement("div", { style: render ? {} : { display: 'none' }, className: "uitk-static-eg-maps-container", id: "uitk-static-eg-maps-container-" + idSuffix, ref: mapRef }));
};

/**
 * Business-level static eg-maps component
 *
 * Warning: EG Maps does not support Static Maps on Google.
 * If you are looking to implement a static map, please reach out to the team in #eg-maps.
 */
var UitkStaticEGMaps = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h;
    var children = _a.children, rest = __rest(_a, ["children"]);
    var scriptLoaderProps = {
        accessToken: ((_c = (_b = rest.staticMapConfig) === null || _b === void 0 ? void 0 : _b.googleMaps) === null || _c === void 0 ? void 0 : _c.apiKey) || '',
        googleMapsId: ((_e = (_d = rest.staticMapConfig) === null || _d === void 0 ? void 0 : _d.googleMaps) === null || _e === void 0 ? void 0 : _e.mapStyle) || '57df04e09ffe3110',
        locale: ((_g = (_f = rest.staticMapConfig) === null || _f === void 0 ? void 0 : _f.googleMaps) === null || _g === void 0 ? void 0 : _g.locale) || 'en_US',
        mapType: ((_h = rest.staticMapConfig) === null || _h === void 0 ? void 0 : _h.mapProvider) === 'mapbox' ? UitkEGMapsType.MAPBOX : UitkEGMapsType.GOOGLE
    };
    return withScriptLoader(React.createElement(UitkStaticEgMapsContainer, __assign({}, rest), children), __assign({}, scriptLoaderProps));
};

export { AirportMarker, CarMarker, CircleMarker, HotelMarker, Marker$1 as Marker, PlaceMarker, PriceMarker, RestaurantMarker, SoldOutMarker, TransitMarker, UitkEGMaps, UitkEGMapsContainer, UitkEGMapsContext, UitkEGMapsDistanceMode, UitkEGMapsIconType, UitkEGMapsMarkerAction, UitkEGMapsMarkerType, UitkEGMapsPlaceType, UitkEGMapsPropertyStatus, UitkEGMapsRouteAction, UitkEGMapsScriptLoader, UitkEGMapsType, UitkEGMapsUitkIconType, UitkMap, UitkMarker, UitkStaticEGMaps, UitkStaticEgMapsContainer, buildIcon, centerAndZoom, concatPromises, constants, defaultEGMapsConfig, defineLocationPopup, defineMarkerSvg, fitBounds, getBounds, getCenter, initMapBoxApi, modifyMarkerObj, onMapBoundsChanged, onMapHoverChanged, onMapIdle, onMapMarkerChange, onMapSelectionChange, onMapTilesLoad, onRouteChange, renderMarkers, useIsMounted, withScriptLoader };
